import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { CustomInput } from 'components';
import OutsideClickHandler from 'components/outside-click-handler/OutsideClickHandler';
import { REGEX } from 'constants/common';
import { useValidateAddressMutation } from 'containers/customers/api';
import { initialAddressState } from 'containers/customers/constants';
import { useGetStatesListQuery } from 'containers/team-profile/api';
import { ParsedAddress, clevertapEvents } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { googleAdressParser } from 'utils/maps';

interface AddressSectionProps {
  onAddressChange: (address: ParsedAddress) => void;
  initialAddress?: ParsedAddress;
  isDisabled?: boolean;
  inputClassName?: string;
}

export interface AddressSectionRef {
  validateAddress: () => Promise<boolean>;
}

enum AddressFields {
  street = 'street',
  apartment = 'apartment',
  city = 'city',
  state = 'state',
  zipCode = 'zipCode'
}

const errorsInitialState = {
  state: '',
  zipCode: '',
  city: '',
  street: '',
  apartment: ''
};

const AddressSection = forwardRef<AddressSectionRef, AddressSectionProps>(
  ({ onAddressChange, initialAddress, isDisabled = false, inputClassName = '' }, ref) => {
    const [addressString, setAddressString] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [fullAddress, setFullAddress] = useState<ParsedAddress>(initialAddress || initialAddressState);
    const [suggestedAddress, setSuggestedAddress] = useState<ParsedAddress>(initialAddressState);
    const [errors, setErrors] = useState<Record<AddressFields, string>>(errorsInitialState);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const autoCompleteRef = useRef(null);
    const sessionToken = useRef(null);
    const placesService = useRef(null);
    const suggestionsCache = useRef({});

    const { data: statesResponse } = useGetStatesListQuery(
      {},
      {
        // Keep the data fresh for longer
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false
      }
    );
    const [validateAddress] = useValidateAddressMutation();

    const statesList = statesResponse?.data?.states || [];

    useEffect(() => {
      if (!autoCompleteRef.current) {
        if (window.google?.maps?.places) {
          autoCompleteRef.current = new window.google.maps.places.AutocompleteService();
          placesService.current = new window.google.maps.places.PlacesService(document.createElement('div'));
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        } else {
          logAnalyticEvent(clevertapEvents.WebGoogleMapsScriptLoaded, {
            'Google Maps Script Loaded': false
          });
        }
      }

      return () => {
        const input = document.getElementById('cof-autocomplete-address-input');
        if (input) window.google?.maps.event?.clearInstanceListeners(input);
        sessionToken.current = null;
      };
    }, []);

    useEffect(() => {
      if (initialAddress && initialAddress.street) {
        const { street, apartment, city, country, zipCode, state, latitude, longitude } = initialAddress;
        setFullAddress({ street, apartment, city, country, zipCode, state, latitude, longitude });
        setSuggestedAddress({ street, apartment, city, country, zipCode, state });
        setAddressString(street || '');
      }
    }, [initialAddress?.street]);

    useEffect(() => {
      onAddressChange(fullAddress);
    }, [fullAddress]);

    // Auto-scroll dropdown into view when suggestions appear
    useEffect(() => {
      if (showSuggestions && suggestions.length > 0 && dropdownRef.current) {
        // Small delay to ensure dropdown is fully rendered
        const timeoutId = setTimeout(() => {
          const dropdown = dropdownRef.current;
          if (!dropdown) return;

          // Find the scrollable content area (should be the direct scrollable parent now)
          const modalScrollContainer = dropdown.closest('.overflow-y-auto');

          if (modalScrollContainer) {
            // Use scrollIntoView which is more reliable for the new structure
            dropdown.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest'
            });
          }
        }, 10);

        return () => clearTimeout(timeoutId);
      }
    }, [showSuggestions, suggestions]);

    const debouncedPredictions = useRef(
      debounce((input, callback) => {
        const options = {
          componentRestrictions: { country: 'us' },
          fields: ['address_components'],
          strictBounds: false,
          types: ['address']
        };

        if (suggestionsCache.current[input]) {
          callback(suggestionsCache.current[input]);
          return;
        }

        if (input && autoCompleteRef.current) {
          autoCompleteRef.current.getPlacePredictions(
            { input, sessionToken: sessionToken.current, ...options },
            (predictions, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                suggestionsCache.current[input] = predictions;
                callback(predictions);
              } else {
                callback([]);
              }
            }
          );
        } else {
          callback([]);
        }
      }, 600)
    ).current;

    const addAddressAnalytics = (input, addressSuggestions) => {
      logAnalyticEvent(clevertapEvents.WebAddressPredictions, {
        'Address Predictions': JSON.stringify(addressSuggestions.map(suggestion => suggestion.description)),
        'Search Input': input
      });
    };

    const handleInputChange = value => {
      setAddressString(value);
      setShowSuggestions(value.length >= 3);

      const cachedPrefix = Object.keys(suggestionsCache.current).find(
        key => value.startsWith(key) && suggestionsCache.current[key]?.length > 0
      );

      if (cachedPrefix) {
        const filteredSuggestions = suggestionsCache.current[cachedPrefix].filter(
          suggestion => suggestion.description.toLowerCase().includes(value.toLowerCase())
          // eslint-disable-next-line function-paren-newline
        );

        if (filteredSuggestions.length > 0) {
          setSuggestions(filteredSuggestions);
          addAddressAnalytics(value, filteredSuggestions);
          return;
        }
      }

      if (value.length >= 3) {
        debouncedPredictions(value, predictions => {
          setSuggestions(predictions);
          addAddressAnalytics(value, predictions);
        });
        return;
      }

      setSuggestions([]);
      if (!value) {
        setErrors(prevState => ({ ...prevState, street: 'Please enter a valid street name.' }));
      } else {
        setErrors(prevState => ({ ...prevState, street: '' }));
      }
    };

    const handleSuggestionClick = placeSuggestion => {
      const { place_id: placeId, description } = placeSuggestion;
      setAddressString(description);
      setShowSuggestions(false);

      placesService.current?.getDetails(
        {
          placeId,
          fields: ['address_components', 'geometry'],
          sessionToken: sessionToken.current
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const address: ParsedAddress = {
              ...googleAdressParser(place.address_components),
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng()
            };

            setFullAddress(address);
            setSuggestedAddress(address);
            setErrors(errorsInitialState);
            sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
          }
        }
      );
    };

    const handleAddressChange = async (key, value) => {
      setErrors(prevState => ({ ...prevState, [key]: '' }));
      if (key === AddressFields.state) {
        setErrors(prevState => ({ ...prevState, city: '' }));
      }
      if (key === AddressFields.zipCode) {
        if (REGEX.zipCodeOnChange.test(value) || value === '') {
          setFullAddress(prevState => ({ ...prevState, [key]: value }));
        }
      } else {
        setFullAddress(prevState => ({ ...prevState, [key]: value }));
      }

      // Validate address if state or city changed
      if (key === AddressFields.state || key === AddressFields.city) {
        const updatedAddress = { ...fullAddress, [key]: value };
        if (updatedAddress.state !== suggestedAddress.state || updatedAddress.city !== suggestedAddress.city) {
          if (updatedAddress.state && checkIfValidState(updatedAddress.state)) {
            try {
              await validateAddress({
                street: updatedAddress.street?.trim(),
                apartment: updatedAddress.apartment?.trim(),
                city: updatedAddress.city?.trim(),
                state: updatedAddress.state?.trim(),
                stateCode: getStateCode(updatedAddress.state),
                zipCode: updatedAddress.zipCode?.trim(),
                country: updatedAddress.country?.trim()
              }).unwrap();
            } catch (error) {
              if (key === AddressFields.city) {
                setErrors(prevState => ({ ...prevState, city: 'Please enter a valid city.' }));
              }
            }
          } else if (updatedAddress.state && !checkIfValidState(updatedAddress.state)) {
            setErrors(prevState => ({ ...prevState, state: 'Please enter a valid state.' }));
          }
        }
      }
    };

    const checkIfValidState = addressState => {
      const isValidState = statesList.some(
        state =>
          state.name.toLowerCase() === addressState.toLowerCase() ||
          state.state_code.toLowerCase() === addressState.toLowerCase()
      );
      return isValidState;
    };

    const getStateCode = state => {
      const foundState = statesList.find(
        stateItem =>
          stateItem.name.toLowerCase() === state.trim().toLowerCase() ||
          stateItem.state_code.toLowerCase() === state.trim().toLowerCase()
      );
      return foundState?.state_code || state;
    };

    const validateAddressFields = async () => {
      let isValid = true;

      // Street address is now mandatory
      if (!addressString || !addressString.trim()) {
        setErrors(prevState => ({ ...prevState, street: 'Please enter a valid street name.' }));
        isValid = false;
      } else if (addressString.trim() !== fullAddress.street?.trim()) {
        setErrors(prevState => ({ ...prevState, street: 'Please enter a valid street name.' }));
        isValid = false;
      }

      // Validate zip code if it exists
      if (!REGEX.zipCode.test(fullAddress.zipCode)) {
        setErrors(prevState => ({ ...prevState, zipCode: 'Please enter a valid zip code.' }));
        isValid = false;
      }

      // Validate state and city if they differ from suggested address
      if (fullAddress.state !== suggestedAddress.state || fullAddress.city !== suggestedAddress.city) {
        if (!checkIfValidState(fullAddress.state) || !fullAddress.state) {
          setErrors(prevState => ({ ...prevState, state: 'Please enter a valid state.' }));
          isValid = false;
        }

        if (fullAddress.state && checkIfValidState(fullAddress.state)) {
          try {
            await validateAddress({
              street: fullAddress.street?.trim(),
              apartment: fullAddress.apartment?.trim(),
              city: fullAddress.city?.trim(),
              state: fullAddress.state?.trim(),
              stateCode: getStateCode(fullAddress.state),
              zipCode: fullAddress.zipCode?.trim(),
              country: fullAddress.country?.trim()
            }).unwrap();
          } catch (error) {
            setErrors(prevState => ({ ...prevState, city: 'Please enter a valid city.' }));
            isValid = false;
          }
        }
      }

      return isValid;
    };

    useImperativeHandle(ref, () => ({
      validateAddress: validateAddressFields
    }));

    const showExpanded = fullAddress?.city || fullAddress?.state || fullAddress?.zipCode;

    return (
      <div className="space-y-4">
        <div className="relative">
          <div className="mb-2.5 text-sbase font-bold text-primaryText">
            Street<span className="text-error">*</span>
          </div>
          <div className={errors.street ? 'pb-2' : ''}>
            <CustomInput
              value={addressString}
              onChange={handleInputChange}
              id="cof-autocomplete-address-input"
              placeholder="Enter street address"
              disabled={isDisabled}
              className={`border border-borderGray outline-none ${inputClassName}`}
            />
            {errors.street && <div className="absolute mt-1 text-xs text-error">{errors.street}</div>}
          </div>

          {showSuggestions && (
            <OutsideClickHandler onOutsideClick={() => setShowSuggestions(false)}>
              <div
                ref={dropdownRef}
                className="absolute inset-x-0 top-full z-50 mt-1 rounded border border-borderGray bg-white shadow-lg">
                {suggestions?.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto">
                    {suggestions.map(suggestion => (
                      <div
                        className="cursor-pointer border-b border-gray-100 p-3 last:border-b-0 hover:bg-slate-100"
                        key={suggestion.place_id}
                        onClick={() => handleSuggestionClick(suggestion)}>
                        <div className="text-sm text-primaryText">{suggestion.description}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 text-center text-sm text-primaryText">No results found</div>
                )}
              </div>
            </OutsideClickHandler>
          )}
        </div>

        {showExpanded && (
          <>
            <div className="flex items-start gap-4">
              <div className="w-1/2">
                <div className="mb-2.5 text-sbase font-semibold text-primaryText">Apt/Suite/Unit</div>
                <div>
                  <CustomInput
                    id="cof-apartment"
                    value={fullAddress.apartment}
                    onChange={value => handleAddressChange(AddressFields.apartment, value)}
                    placeholder="Apt/Suite/Unit"
                    disabled={isDisabled}
                    className={`border border-borderGray outline-none ${inputClassName}`}
                  />
                </div>
              </div>
              <div className="w-1/2">
                <div className="mb-2.5 text-sbase font-semibold text-primaryText">
                  City<span className="text-error">*</span>
                </div>
                <div>
                  <CustomInput
                    id="cof-city"
                    value={fullAddress.city}
                    onChange={value => handleAddressChange(AddressFields.city, value)}
                    placeholder="Enter city"
                    disabled={isDisabled}
                    className={`border border-borderGray outline-none ${inputClassName}`}
                  />
                  {errors.city && <div className="mt-1 text-xs text-error">{errors.city}</div>}
                </div>
              </div>
            </div>

            <div className={`flex items-start gap-4 ${errors.state || errors.zipCode ? 'pb-3' : ''}`}>
              <div className="w-1/2">
                <div className="mb-2.5 text-sbase font-semibold text-primaryText">
                  State<span className="text-error">*</span>
                </div>
                <div>
                  <CustomInput
                    id="cof-state"
                    value={fullAddress.state}
                    onChange={value => handleAddressChange(AddressFields.state, value)}
                    placeholder="Enter state"
                    disabled={isDisabled}
                    className={`border border-borderGray outline-none ${inputClassName}`}
                  />
                  {errors.state && <div className="mt-1 text-xs text-error">{errors.state}</div>}
                </div>
              </div>
              <div className="w-1/2">
                <div className="mb-2.5 text-sbase font-semibold text-primaryText">
                  Zip code<span className="text-error">*</span>
                </div>
                <div>
                  <CustomInput
                    id="cof-zip-code"
                    value={fullAddress.zipCode}
                    onChange={value => handleAddressChange(AddressFields.zipCode, value)}
                    placeholder="Enter zip code"
                    disabled={isDisabled}
                    className={`border border-borderGray outline-none ${inputClassName}`}
                  />
                  {errors.zipCode && <div className="mt-1 text-xs text-error">{errors.zipCode}</div>}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);

AddressSection.displayName = 'AddressSection';

export default AddressSection;
